import { Injectable, InjectionToken } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { NbAuthJWTToken, NbAuthService, NbTokenService } from '@nebular/auth';
import { Action, ActionCreator, Store } from '@ngrx/store';
import { combineLatest, from, map, of, switchMap, tap } from 'rxjs';

import { Auth, User, authState } from '@angular/fire/auth';
import {
  noopAuth,
  resetAuth,
  setAuthToken,
  setAuthUser,
  toAuthTokenClaims,
} from '@vmspdb/ui/store';

import { inject } from '@angular/core';
import { FirebaseUser } from '@vmspdb/ui/domain';

export const AUTH_DISPATCHER_SETTINGS = new InjectionToken<
  ActionCreator<any, () => Action<any>>
>('Auth Dispatcher');

export const AUTH_DISPATCHER_SETTINGS_NOOP = noopAuth;

@Injectable()
export class AuthGuard {
  private authService = inject(NbAuthService);
  private tokenService = inject(NbTokenService);
  private router = inject(Router);
  private store = inject(Store);
  private firebaseAuth = inject(Auth);

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    action: ActionCreator<any, () => Action<any>>,
  ) {
    return combineLatest([
      this.authService.isAuthenticated(),
      authState(this.firebaseAuth),
    ]).pipe(
      tap(([, userObject]) => {
        if (!userObject) {
          // docs/e2e-backlog.md#bug-025 - the return url travels on the
          // resetAuth action itself (consumed by resetAuth$'s navigate) so
          // it survives the reentrant navigation cancellation traced there,
          // rather than through a separate setAuthReturnUrl dispatch in the
          // switchMap below, which never got the chance to run.
          this.store.dispatch(resetAuth({ returnUrl: state.url }));
          return;
        }
        this.setCurrentUser(userObject);
      }),
      switchMap(([authenticated, userObject]) => {
        if (authenticated) {
          this.store.dispatch(action());
          return of(true);
        }

        if (userObject) {
          return from(userObject.getIdTokenResult()).pipe(
            switchMap((idToken) => {
              const nbToken = new NbAuthJWTToken(
                idToken.token,
                idToken.signInProvider ?? '',
              );
              return this.tokenService.set(nbToken).pipe(
                map(() => nbToken.isValid()),
                tap((valid) => {
                  if (valid) {
                    this.store.dispatch(action());
                  }
                }),
              );
            }),
          );
        }

        // No Firebase user at all: the tap above already dispatched
        // resetAuth({ returnUrl: state.url }), whose effect performs the
        // actual redirect to /auth/login. Nothing left to activate here.
        return of(false);
      }),
    ); // canActive can return Observable<boolean>, which is exactly what isAuthenticated returns
  }

  private setCurrentUser(u: User) {
    this.store.dispatch(setAuthUser({ user: u.toJSON() as FirebaseUser }));
    u.getIdTokenResult().then((result) => {
      this.store.dispatch(setAuthToken({ token: toAuthTokenClaims(result) }));
    });
  }
}

export const authGuardCanActivate = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const action = inject(AUTH_DISPATCHER_SETTINGS);
  const authService = inject(AuthGuard);
  return authService.canActivate(route, state, action);
};
